import { DestroyRef, Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { Observable } from 'rxjs/internal/Observable';
import { catchError, filter } from 'rxjs/operators';

import { ErrorService } from '@shared/services/error.service';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { Subject } from 'rxjs';
import { pages } from '../../shared/config/pages';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private instanceId: string;

  private history: string[] = [];

  public setSidenav: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private errorService: ErrorService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  getCurrentPage() {
    return this.router.url;
  }

  routeChanges(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd),
      catchError((error: any) => {
        return error;
      })
    ) as Observable<NavigationEnd>;
  }

  initNavigation(event: NavigationEnd): Promise<void> {
    if (this.authenticationService.isAuthenticated()) {
      return this.navigateTo(event.url);
    } else {
      return this.navigateTo(pages.login);
    }
  }

  hideSideNavInCurrentPage(event: NavigationEnd): boolean {
    return (
      event.url === pages.login ||
      event.url === pages.signIn ||
      event.url === pages.firstLogin ||
      event.url === pages.resetRequest ||
      event.url.includes('/reset-password/') ||
      event.url === pages.fullscreenHome ||
      ((event.url.includes('/hrsuite/liste') || event.url.includes('/smart-planning/week-vue')) &&
        event.url.includes('scenario') &&
        event.url.includes('campaign'))
    );
  }

  navigateTo(
    page: string,
    data?: any,
    replaceUrl: boolean = true,
    state: any = undefined,
    fragment: any = undefined
  ): Promise<void> {
    (document.body.style as any).zoom = '100%'; //Reset zoom

    return new Promise((resolve: any, reject: any) => {
      if (this.getCurrentPage() === page) {
        resolve();
      }

      this.router
        .navigate([page], { queryParams: data, replaceUrl, state: state, fragment: fragment })
        .then((success: boolean) => {
          if (success || page != this.getCurrentPage()) {
            resolve();
          } else {
            reject(new Error('navigation failed'));
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  reloadCurrentRoute() {
    const currentUrl = this.router.url;
    return this.router.navigate([currentUrl], { onSameUrlNavigation: 'reload' });
  }

  navigateByUrl(page: string, event?: NavigationEnd | undefined, data?: any): Promise<void> {
    const currentPage = this.currentPage(event);
    if (page !== currentPage && !(currentPage === pages.root && page === pages.defaultRoute)) {
      const urlWithParams = data ? `${page}?${new URLSearchParams(data)}` : page;
      return this.router.navigateByUrl(urlWithParams, { replaceUrl: true }).then((success: boolean) => {
        if (!success) {
          console.error('navigation failed');
        }
      });
    }
    return Promise.resolve();
  }

  loadRouting(): void {
    this.subscriptionManager
      .createRequest(this.router.events, 'routerEvents', this.instanceId)
      .pipe(filter((event: Event) => event instanceof NavigationEnd))
      .subscribe({
        next: ({ urlAfterRedirects }: NavigationEnd) => {
          this.history = [...this.history, urlAfterRedirects];
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  getPreviousUrl(): string {
    return this.history[this.history.length - 2] || '/';
  }

  private currentPage(event: NavigationEnd | undefined): string | null {
    return event ? event.url : null;
  }

  clear() {
    this.history = [];
    this.setSidenav.complete();
    this.setSidenav = new Subject<void>();
  }
}
